import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Mic, Pause, Play, Square, X, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { uploadRecording } from "@/lib/api";
import WalkinSummary from "@/components/WalkinSummary";

// Sales mode: record a consult in the app, keep it safe on the phone while recording,
// upload it to cloud storage on Stop, then hand the saved recording to the New consult form.
//
// Audio chunks are written to IndexedDB every few seconds, so a crash, accidental close or
// dead battery doesn't lose the call: the next time Sales mode opens it offers to recover it.
//
// Consent is asked on tape: the rep taps Start, the screen says the call is recorded, and the
// rep asks the member out loud. "Agreed" keeps recording (the yes is the proof); "Declined"
// throws the audio away on the phone before anything is uploaded.

const DB_NAME = "fpa-sales-mode";
const STORE = "chunks";

function idb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { autoIncrement: true });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbAdd(value) {
  try {
    const db = await idb();
    await new Promise((res, rej) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).add(value);
      tx.oncomplete = res; tx.onerror = () => rej(tx.error);
    });
  } catch { /* storage unavailable (private mode): recording still works in memory */ }
}
async function idbAll() {
  try {
    const db = await idb();
    return await new Promise((res, rej) => {
      const req = db.transaction(STORE, "readonly").objectStore(STORE).getAll();
      req.onsuccess = () => res(req.result || []); req.onerror = () => rej(req.error);
    });
  } catch { return []; }
}
async function idbClear() {
  try {
    const db = await idb();
    await new Promise((res) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).clear();
      tx.oncomplete = res; tx.onerror = res;
    });
  } catch { /* ignore */ }
}

// WebM/Opus first: Chrome and Edge record it reliably. Windows Chrome also claims audio/mp4
// but can hand back empty recordings with it (CR-0028); Safari only has mp4, so it's the fallback.
const pickMime = () => {
  const options = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
  return options.find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported?.(m)) || "";
};
const extFor = (mime) => (mime.includes("mp4") ? "m4a" : mime.includes("ogg") ? "ogg" : "webm");
const ASK = "Before we start: we record consults for training and quality, to make sure I give you the best outcome for your training history and goals. It stays inside LIFTR and is never shared. Is that okay with you?";
const clock = (s) => `${Math.floor(s / 3600) ? `${Math.floor(s / 3600)}:` : ""}${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

// practice=true: a training role play. Saved with source "practice" so it never counts as a consult.
// onDeclined: the member said no on tape; the audio is already deleted from the phone.
// preAgreed: the member said yes on the pre-sale questionnaire, so recording starts by itself.
// questionnaire: the member's pre-sale questionnaire, shown beside the controls while recording
// so the coach can use their answers in the conversation (CR-0018).
export default function SalesMode({ onClose, onSaved, onDeclined, practice = false, preAgreed = false, questionnaire = null }) {
  const [phase, setPhase] = useState("consent");   // consent | asking | recording | paused | saving | recover
  const agreed = useRef(practice || preAgreed);     // false while the member hasn't said yes yet
  const [seconds, setSeconds] = useState(0);
  const [progress, setProgress] = useState(0);
  const [recovered, setRecovered] = useState(null); // {blob, mime, seconds}
  const [noAudio, setNoAudio] = useState(false);     // recording, but nothing captured yet
  const recoveredUrl = useMemo(() => (recovered && !recovered.empty ? URL.createObjectURL(recovered.blob) : null), [recovered]);
  useEffect(() => () => { if (recoveredUrl) URL.revokeObjectURL(recoveredUrl); }, [recoveredUrl]);
  const rec = useRef(null);
  const stream = useRef(null);
  const chunks = useRef([]);
  const mime = useRef("");
  const timer = useRef(null);
  const wakeLock = useRef(null);
  const startedAt = useRef(0);
  const elapsedBefore = useRef(0);

  // Anything left over from a recording that never got saved? If not, and the member already
  // agreed on the questionnaire, start recording straight away.
  useEffect(() => {
    idbAll().then(async (rows) => {
      let parts = rows.filter((r) => r && r.blob);
      // Closed before the member answered the recording question: never offer to keep it.
      if (parts.length && parts[parts.length - 1].agreed === false) { await idbClear(); parts = []; }
      if (!parts.length) { if (preAgreed) start(); return; }
      const m = parts[0].mime || "audio/webm";
      setRecovered({ blob: new Blob(parts.map((p) => p.blob), { type: m }), mime: m, seconds: parts[parts.length - 1].seconds || 0 });
      setPhase("recover");
    });
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const keepAwake = useCallback(async () => {
    try { wakeLock.current = await navigator.wakeLock?.request("screen"); } catch { /* not supported */ }
  }, []);
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible" && rec.current?.state === "recording") keepAwake(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [keepAwake]);

  const tick = () => setSeconds(elapsedBefore.current + (Date.now() - startedAt.current) / 1000);

  const stopEverything = () => {
    clearInterval(timer.current);
    try { stream.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    try { wakeLock.current?.release(); } catch { /* ignore */ }
  };
  useEffect(() => () => stopEverything(), []);

  const starting = useRef(false);
  const start = async () => {
    if (starting.current || rec.current) return;   // the auto-start and a tap can't both start one
    starting.current = true;
    try { await begin(); } finally { starting.current = false; }
  };
  const begin = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      toast.error("This browser can't record audio. Update your phone's browser, or record with the phone's voice recorder and upload the file instead.");
      return;
    }
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch {
      toast.error("FPA needs microphone access. Allow it in the browser's site settings and try again.");
      return;
    }
    await idbClear();
    chunks.current = [];
    agreed.current = practice || preAgreed;
    mime.current = pickMime();
    const r = new MediaRecorder(stream.current, { ...(mime.current ? { mimeType: mime.current } : {}), audioBitsPerSecond: 32000 });
    mime.current = r.mimeType || mime.current || "audio/webm";
    r.ondataavailable = (e) => {
      if (e.data && e.data.size) {
        chunks.current.push(e.data);
        setNoAudio(false);
        idbAdd({ blob: e.data, mime: mime.current, agreed: agreed.current,
          seconds: Math.round(elapsedBefore.current + (Date.now() - startedAt.current) / 1000) });
      }
    };
    r.onerror = (ev) => toast.error(`Recording error: ${ev?.error?.message || "the browser stopped recording"}. Stop & save what's there.`);
    rec.current = r;
    r.start(5000);                    // a chunk every 5 seconds, saved on the phone as it goes
    // No audio after 15 seconds (muted or missing mic, or the browser's recorder failing):
    // say so now rather than after a 20-minute consult.
    setNoAudio(false);
    setTimeout(() => {
      if (rec.current === r && r.state !== "inactive" && !chunks.current.some((c) => c.size > 0)) setNoAudio(true);
    }, 15000);
    startedAt.current = Date.now();
    elapsedBefore.current = 0;
    timer.current = setInterval(tick, 500);
    keepAwake();
    setPhase(agreed.current ? "recording" : "asking");
  };

  const memberAgreed = () => {
    agreed.current = true;
    setPhase("recording");
  };

  // Member said no: stop, and delete every bit of audio on the phone. Nothing is uploaded.
  const memberDeclined = () => {
    const r = rec.current;
    if (r) {
      r.ondataavailable = null;                // the last chunk must not be written after the clear
      r.onstop = () => { idbClear(); };
      try { r.state !== "inactive" && r.stop(); } catch { /* ignore */ }
    }
    stopEverything();
    chunks.current = [];
    rec.current = null;
    idbClear();
    toast.success("Recording deleted. Nothing was saved.");
    (onDeclined || onClose)();
  };

  const pause = () => {
    rec.current?.pause();
    elapsedBefore.current += (Date.now() - startedAt.current) / 1000;
    clearInterval(timer.current);
    setPhase("paused");
  };
  const resume = () => {
    rec.current?.resume();
    startedAt.current = Date.now();
    timer.current = setInterval(tick, 500);
    setPhase("recording");
  };

  const upload = async (blob, m, secs) => {
    if (!blob || !blob.size) {
      // Nothing in memory: try what was saved on the phone as the call went.
      const rows = (await idbAll()).filter((r) => r && r.blob && r.blob.size);
      const fromPhone = rows.length ? new Blob(rows.map((r) => r.blob), { type: rows[0].mime || m }) : null;
      if (fromPhone && fromPhone.size) {
        blob = fromPhone;
      } else {
        toast.error("This recording is empty: no audio was captured, so there's nothing to save. Check the microphone isn't muted next time. You can delete it.");
        setRecovered({ blob: blob || new Blob([], { type: m }), mime: m, seconds: secs, empty: true });
        setPhase("recover");
        return;
      }
    }
    setPhase("saving");
    setProgress(0);
    try {
      const stamp = new Date().toISOString().slice(0, 16).replace("T", " ");
      const file = new File([blob], `consult-${stamp.replace(/[: ]/g, "-")}.${extFor(m)}`, { type: m.split(";")[0] });
      const saved = await uploadRecording(`${practice ? "Practice role play" : "Sales mode"} ${stamp}`, null, file, setProgress, null,
        { source: practice ? "practice" : "sales_mode", recorded_seconds: Math.round(secs) });
      await idbClear();
      toast.success(practice ? "Practice role play saved" : "Recording saved to the cloud");
      onSaved({ id: saved.id, name: file.name, seconds: Math.round(secs), practice });
    } catch (e) {
      toast.error(`${e?.response?.data?.detail || "Upload failed"}. The recording is still on this phone; tap Save again to retry.`);
      setRecovered({ blob, mime: m, seconds: secs });
      setPhase("recover");
    }
  };

  const stopAndSave = () => {
    const r = rec.current;
    if (!r) return;
    const secs = phase === "recording" ? elapsedBefore.current + (Date.now() - startedAt.current) / 1000 : elapsedBefore.current;
    r.onstop = () => {
      stopEverything();
      upload(new Blob(chunks.current, { type: mime.current }), mime.current, secs);
    };
    r.stop();
  };

  const discard = async () => {
    if (!window.confirm("Delete this recording? It can't be recovered.")) return;
    stopEverything();
    try { rec.current?.state !== "inactive" && rec.current?.stop(); } catch { /* ignore */ }
    await idbClear();
    onClose();
  };

  const live = phase === "recording" || phase === "paused";
  const asking = phase === "asking";
  const showForm = !!questionnaire && !practice && (live || asking || phase === "consent");
  return (
    <div className="fixed inset-0 z-[60] bg-[#070707] text-zinc-100 flex flex-col" role="dialog" aria-modal="true" aria-label="Sales mode"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="sales-mode">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#1f1f1f]">
        <div className="flex items-center gap-2">
          <span className={`h-2.5 w-2.5 rounded-full ${phase === "recording" || asking ? "bg-rose-500 animate-pulse" : "bg-zinc-600"}`} />
          <span className="font-display font-bold">{practice ? "Practice role play" : "Sales mode"}</span>
        </div>
        {!live && !asking && phase !== "saving" && (
          <button type="button" onClick={onClose} aria-label="Close Sales mode" className="p-2 text-zinc-400 hover:text-white"><X className="h-5 w-5" /></button>
        )}
      </div>

      <div className={showForm ? "flex-1 min-h-0 overflow-y-auto md:overflow-hidden md:grid md:grid-cols-[17rem_minmax(0,1fr)]" : "contents"}>
      <div className={`flex flex-col items-center justify-center text-center ${showForm ? "gap-3 px-4 py-4 md:h-full md:border-r md:border-[#1f1f1f]" : "flex-1 gap-6 px-6"}`}>
        {phase === "consent" && (
          <>
            {practice ? (
              <div className="rounded-xl border border-blue-500/40 bg-blue-500/10 p-4 text-left max-w-sm">
                <div className="font-semibold text-blue-300">Training role play</div>
                <p className="text-sm text-blue-100/90 mt-1.5">
                  For your training homework only. It won&apos;t count as a consult. Hand it in on the Sales Training page afterwards.
                </p>
              </div>
            ) : preAgreed ? (
              <div className={`rounded-xl border border-emerald-500/40 bg-emerald-500/10 text-left max-w-sm ${showForm ? "p-3 text-sm" : "p-4"}`}>
                <div className="font-semibold text-emerald-300">Member agreed on the questionnaire</div>
                <p className="text-sm text-emerald-100/90 mt-1.5">No need to ask again. Tap Start if recording didn&apos;t begin by itself.</p>
              </div>
            ) : (
            <div className={`rounded-xl border border-amber-500/40 bg-amber-500/10 text-left max-w-sm ${showForm ? "p-3 text-sm" : "p-4"}`}>
              <div className="flex items-center gap-2 font-semibold text-amber-300"><ShieldAlert className="h-4 w-4" />Ask on the recording</div>
              <p className="text-sm text-amber-100/90 mt-1.5">
                Tap Start, then ask the member straight away. The member&apos;s answer is recorded.
                If they say no, tap &quot;Member declined&quot; and the recording is deleted from this phone. Nothing is uploaded.
              </p>
            </div>
            )}
            <button type="button" onClick={start} data-testid="sales-mode-start"
              className={`rounded-full bg-rose-600 hover:bg-rose-500 active:scale-95 transition flex flex-col items-center justify-center gap-1 shadow-2xl shadow-rose-900/50 ${showForm ? "h-20 w-20" : "h-36 w-36"}`}>
              <Mic className={showForm ? "h-6 w-6" : "h-10 w-10"} />
              <span className={`font-semibold ${showForm ? "text-[10px] leading-tight px-1" : "text-sm"}`}>{practice || preAgreed ? "Start recording" : "Start · ask the member"}</span>
            </button>
            {!showForm && <p className="text-xs text-zinc-500 max-w-xs">Keep FPA open with the screen on while you record. The call is saved on this phone as you go.</p>}
          </>
        )}

        {asking && (
          <>
            <div className="flex items-center gap-2 text-sm text-rose-300" data-testid="sales-mode-asking">
              <span className="h-2 w-2 rounded-full bg-rose-500 animate-pulse" />Recording · {clock(seconds)}
            </div>
            <div className="max-w-sm text-left">
              <div className="eyebrow text-zinc-400">Say to the member</div>
              <p className="mt-2 text-xl leading-snug font-semibold">&quot;{ASK}&quot;</p>
            </div>
            <div className="flex flex-col gap-3 w-full max-w-sm">
              <Button type="button" onClick={memberAgreed} data-testid="sales-mode-agreed"
                className="h-16 rounded-full bg-emerald-600 hover:bg-emerald-500 text-base font-semibold">
                Member agreed · keep recording
              </Button>
              <Button type="button" variant="outline" onClick={memberDeclined} data-testid="sales-mode-declined"
                className="h-14 rounded-full border-[#333] bg-transparent text-base">
                Member declined · delete recording
              </Button>
            </div>
          </>
        )}

        {live && (
          <>
            <div className={`font-display font-extrabold tabular-nums ${showForm ? "text-3xl" : "text-6xl"}`} data-testid="sales-mode-timer">{clock(seconds)}</div>
            <div className={`text-sm ${phase === "recording" ? "text-rose-300" : "text-amber-300"}`}>{phase === "recording" ? "Recording…" : "Paused"}</div>
            {noAudio && (
              <div className="rounded-lg border border-rose-500/50 bg-rose-500/15 px-3 py-2 text-sm text-rose-100 max-w-xs" role="alert" data-testid="sales-mode-no-audio">
                No audio is being captured. Check the microphone isn&apos;t muted (on a laptop, the mic key), then Stop &amp; save and start again.
              </div>
            )}
            <div className="flex items-center gap-4">
              {phase === "recording" ? (
                <Button type="button" onClick={pause} variant="outline" className={`rounded-full border-[#333] bg-transparent ${showForm ? "h-11 w-11" : "h-14 w-14"}`} aria-label="Pause"><Pause className="h-5 w-5" /></Button>
              ) : (
                <Button type="button" onClick={resume} variant="outline" className={`rounded-full border-[#333] bg-transparent ${showForm ? "h-11 w-11" : "h-14 w-14"}`} aria-label="Resume"><Play className="h-5 w-5" /></Button>
              )}
              <Button type="button" onClick={stopAndSave} data-testid="sales-mode-stop"
                className={`rounded-full bg-blue-600 hover:bg-blue-500 font-semibold ${showForm ? "h-11 px-5 text-sm" : "h-20 px-8 text-base"}`}>
                <Square className="h-5 w-5 mr-2 fill-current" />Stop &amp; save
              </Button>
            </div>
            {practice ? (
              <button type="button" onClick={discard} className="text-xs text-zinc-500 underline">Discard recording</button>
            ) : (
              <button type="button" data-testid="sales-mode-changed-mind"
                onClick={() => { if (window.confirm("Member doesn't want to be recorded? This deletes the recording from this phone. Nothing is uploaded.")) memberDeclined(); }}
                className="text-xs text-zinc-500 underline">Member changed their mind · delete recording</button>
            )}
          </>
        )}

        {phase === "saving" && (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-blue-400" />
            <div className="text-lg font-semibold">Saving to the cloud… {progress}%</div>
            <p className="text-xs text-zinc-500">Don&apos;t close FPA. The New consult form opens next.</p>
          </>
        )}

        {phase === "recover" && recovered && (
          <>
            <div className="text-lg font-semibold">{recovered.empty ? "This recording is empty" : "An unsaved recording is on this phone"}</div>
            <p className="text-sm text-zinc-400">
              {recovered.empty
                ? "The timer ran, but no audio was captured (often a muted or missing microphone). There's nothing to upload."
                : `${clock(recovered.seconds)} long · ${(recovered.blob.size / 1048576).toFixed(1)} MB. Save it to the cloud and attach it to a consult?`}
            </p>
            {!recovered.empty && (
              <a href={recoveredUrl} download={`consult-recording.${extFor(recovered.mime)}`}
                className="text-xs text-blue-400 underline" data-testid="sales-mode-download">Download a copy to this device</a>
            )}
            <div className="flex gap-3">
              {!recovered.empty && <Button type="button" onClick={() => upload(recovered.blob, recovered.mime, recovered.seconds)} className="bg-blue-600 hover:bg-blue-500 rounded-full px-6">Save it</Button>}
              <Button type="button" variant="outline" onClick={async () => { if (window.confirm("Delete this recording? It can't be recovered.")) { await idbClear(); setRecovered(null); setPhase("consent"); } }}
                className="rounded-full border-[#333] bg-transparent">Delete</Button>
            </div>
          </>
        )}
      </div>
      {showForm && (
        <aside className="text-left px-4 py-4 md:px-6 border-t border-[#1f1f1f] md:border-t-0 md:h-full md:overflow-y-auto" data-testid="sales-mode-questionnaire">
          <div className="eyebrow text-zinc-400 mb-3">{questionnaire.name ? `${questionnaire.name}'s questionnaire` : "Questionnaire"}</div>
          <WalkinSummary form={questionnaire} editable={false} compact />
        </aside>
      )}
      </div>
    </div>
  );
}
